import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys, client } from "queryClient";
import { SyncTask } from "types";
import { getRunningOrPendingSyncTaskList } from "modules/synctasks/api";

export const useSyncTaskRunningList = () => {
  const [syncTaskList, setSyncTaskList] = useState<SyncTask[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [QueryKeys.SyncTasks, "running"],
    queryFn: async () => {
      const syncTaskList = await getRunningOrPendingSyncTaskList();

      return syncTaskList;
    },
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  useEffect(() => {
    if (data) {
      setSyncTaskList(data);
    }
  }, [data]);

  useEffect(() => {
    // Subscribe to creation of SyncTask
    const createSub = client.models.SyncTask.onCreate().subscribe({
      next: (data) => {
        console.log("Create SyncTask", data);
        setSyncTaskList((prev) => [data, ...prev]);
      },
      error: (error) => console.warn(error),
    });

    // Subscribe to update of SyncTask
    const updateSub = client.models.SyncTask.onUpdate().subscribe({
      next: (data) => {
        console.log("Update SyncTask", data);
        setSyncTaskList((prev) =>
          prev.map((item) => (item.id === data.id ? data : item)),
        );
      },
      error: (error) => console.warn(error),
    });

    // Subscribe to deletion of SyncTask
    const deleteSub = client.models.SyncTask.onDelete().subscribe({
      next: (data) => {
        console.log("Delete SyncTask", data);
        setSyncTaskList((prev) => prev.filter((item) => item.id !== data.id));
      },
      error: (error) => console.warn(error),
    });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
      deleteSub.unsubscribe();
    };
  }, []);

  const runningSyncTask = syncTaskList.some(
    (syncTask) =>
      syncTask.status === "Running" || syncTask.status === "Pending",
  );

  return {
    syncTaskList,
    runningSyncTask,
    isLoading,
  };
};
